import { useCallback, useState } from 'react';
import { Card, Etiqueta, TituloDePainel } from '../components/Painel';
import { api, ApiError, type ContaDeAnuncioMedida, type IntegrationsResponse } from '../lib/api';
import { useResource } from '../lib/useResource';
import { formatNumber } from '../lib/dataState';
import { formatInstant } from '../lib/format';

/**
 * Saúde de integrações e capacidades externas (AT-150 a AT-155).
 *
 * A tela existe para separar três coisas que colapsam facilmente num número só,
 * e que levam o operador a ações **diferentes**:
 *
 * - **Capacidade não validada** → alguém precisa conferir a documentação oficial
 *   do provedor. Enquanto não conferir, todo caminho que depende dela falha
 *   fechado, e isso é o comportamento correto, não um defeito.
 * - **Provedor nunca chamado** → pede um smoke test, não investigação.
 * - **Provedor falhando** → pede investigação.
 *
 * Mostrar "0% de sucesso" para um provedor que nunca foi chamado mandaria a
 * pessoa investigar um problema que não existe.
 */

const PROVIDER_STATE: Record<string, { label: string; className: string }> = {
  never_called: {
    label: 'Nunca chamado',
    className: 'border-l-4 border-l-line-strong bg-surface-sunken/60',
  },
  healthy: { label: 'Saudável', className: 'border-l-4 border-l-ok' },
  degraded: { label: 'Degradado', className: 'border-l-4 border-l-warn bg-warn-surface/40' },
  failing: { label: 'Falhando', className: 'border-l-4 border-l-danger bg-danger-surface/60' },
};

const CAPABILITY_STATE: Record<string, string> = {
  available: 'Validada e disponível',
  not_validated: 'Nunca validada',
  unavailable: 'Indisponível',
  degraded: 'Degradada',
};

/**
 * O DIAGNÓSTICO do §139, em linguagem de operação e com o dono da ação.
 *
 * Existe porque os oito estados dão no mesmo resultado (não chamar) por motivos
 * que pedem gente diferente: pedir permissão resolve `PERMISSION_MISSING` e não
 * resolve `ACCOUNT_RESTRICTED`, e `TEMPORARILY_UNAVAILABLE` volta sozinho. Uma
 * linha só dizendo "indisponível" não diz a quem recorrer.
 */
const CAPABILITY_STATUS: Record<string, string> = {
  SUPPORTED: 'funciona',
  UNSUPPORTED: 'o provedor não oferece',
  NOT_TESTED: 'nunca verificada',
  ACCOUNT_RESTRICTED: 'a conta está restrita (resolve quem administra a conta)',
  PERMISSION_MISSING: 'falta permissão no token (resolve quem conecta a conta)',
  DEPRECATED: 'descontinuada pelo provedor (exige mudança nossa)',
  TEMPORARILY_UNAVAILABLE: 'instabilidade do provedor (costuma passar sozinho)',
  UNKNOWN: 'não sabemos',
};

export function Integrations() {
  /*
    A tela recarrega depois da sondagem. Sem isto, quem clicasse em verificar
    veria o resultado da verificacao e a tabela continuaria com os vereditos de
    antes: duas verdades na mesma tela, e a de baixo e a que a pessoa acredita.
  */
  const [versao, setVersao] = useState(0);
  const recarregar = useCallback(() => setVersao((v) => v + 1), []);
  const resource = useResource<IntegrationsResponse>(() => api.integrations(), [versao]);

  if (resource.kind === 'loading') {
    return (
      <div className="text-sm" aria-busy="true">
        <h1 className="text-2xl font-bold">Integrações</h1>
        <p className="mt-2 text-ink-muted">Consultando o histórico…</p>
      </div>
    );
  }

  if (resource.kind === 'error') {
    return (
      <div className="max-w-3xl space-y-4 text-sm">
        <h1 className="text-2xl font-bold">Integrações</h1>
        <div className="mt-4 rounded border border-danger-border bg-danger-surface p-4">
          <p className="font-medium">Não foi possível carregar a saúde das integrações.</p>
          <p className="mt-1">{resource.message}</p>
          <p className="mt-2 text-xs text-ink-faint">
            Nenhum provedor aparece como saudável aqui: não saber o estado nunca é o mesmo que estar
            bem. Código: {resource.code}
          </p>
        </div>
      </div>
    );
  }

  const { data } = resource;

  return (
    <div className="space-y-4 text-sm">
      <header>
        <h1 className="text-2xl font-bold">Integrações</h1>
        <p className="mt-1 text-ink-muted">
          Janela de {formatNumber(data.window_days)} dias, desde {formatInstant(data.since)}.
        </p>
      </header>

      <SondagemDaMeta dados={data} aoSondar={recarregar} />

      {data.unvalidated_capabilities > 0 ? (
        <p className="mt-4 rounded-[11px] border border-line bg-surface-sunken p-3 text-xs text-ink-muted">
          {formatNumber(data.unvalidated_capabilities)} capacidade(s) ainda não foram validadas
          contra a documentação oficial do provedor. Todo caminho que depende delas falha fechado:
          isso é o comportamento correto, e não uma pendência de código.
        </p>
      ) : null}

      <section aria-labelledby="provedores">
        {/*
          GRADE de cards, e nao lista empilhada: o mockup desenha os provedores
          em tres colunas, com as capacidades daquele provedor dentro do card
          dele. Empilhado em largura cheia, cada provedor virava uma faixa de
          uma linha e a tela nao cabia numa olhada.
        */}
        <TituloDePainel id="provedores" texto="Provedores" className="mb-3" />
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {data.providers.map((p) => {
            const estado = PROVIDER_STATE[p.state] ?? {
              label: p.state,
              className: 'border-l-4 border-l-line-strong',
            };
            return (
              <li
                key={p.provider}
                className={`rounded-card border border-line bg-card p-4 shadow-card ${estado.className}`}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-medium">{p.provider}</span>
                  <span className="text-xs uppercase tracking-wide text-ink-muted">
                    {estado.label}
                  </span>
                </div>

                {p.total_calls === null ? (
                  /*
                    "Nunca chamado" NÃO mostra números. Escrever "0 sucessos, 0
                    falhas" convidaria a ler como desempenho ruim, quando na
                    verdade não houve medição nenhuma.
                  */
                  <p className="mt-2 text-xs text-ink-muted">
                    Nenhuma chamada registrada nesta janela. Isso não é desempenho ruim: é ausência
                    de medida. O próximo passo é um smoke test, não investigação.
                  </p>
                ) : (
                  <>
                    <p className="mt-2 text-xs text-ink-muted">
                      {formatNumber(p.total_calls)} chamada(s) · {formatNumber(p.successes)} com
                      sucesso · {formatNumber(p.failures)} com falha · {formatNumber(p.ambiguous)}{' '}
                      ambígua(s)
                    </p>
                    {p.last_call_at ? (
                      <p className="mt-1 text-xs text-ink-faint">
                        Última chamada: {formatInstant(p.last_call_at)}
                      </p>
                    ) : null}
                    {p.last_failure_at ? (
                      <p className="mt-1 text-xs text-warn-ink">
                        Última falha: {formatInstant(p.last_failure_at)}
                        {p.last_failure_code ? ` (${p.last_failure_code})` : ''}
                      </p>
                    ) : null}
                  </>
                )}

                {/*
                  As capacidades DAQUELE provedor, dentro do card dele.

                  O mockup desenha assim (Publishing VERIFIED, Insights
                  VERIFIED, Messaging NOT TESTED) porque e a pergunta que quem
                  opera faz: "o que este provedor consegue fazer agora?". A
                  tabela geral continua abaixo, para quem precisa do detalhe.
                */}
                {(() => {
                  const daqui = data.capabilities.filter((c) => c.provider === p.provider);
                  if (daqui.length === 0) return null;
                  return (
                    <ul className="mt-3 flex flex-wrap gap-1.5">
                      {daqui.slice(0, 6).map((c) => (
                        <li key={c.capability}>
                          <Etiqueta tom={c.state === 'available' ? 'ok' : 'warn'}>
                            {c.capability} {c.status}
                          </Etiqueta>
                        </li>
                      ))}
                      {daqui.length > 6 && (
                        <li className="self-center text-[11px] text-ink-faint">
                          e mais {daqui.length - 6} na tabela abaixo
                        </li>
                      )}
                    </ul>
                  );
                })()}

                {p.unresolved_external_effects > 0 ? (
                  <p className="mt-2 rounded bg-danger-surface p-2 text-xs text-danger-ink">
                    {formatNumber(p.unresolved_external_effects)} efeito(s) externo(s) com resultado
                    desconhecido. Pode haver trabalho pago ou publicado que o sistema não
                    contabilizou. Reconciliar antes de qualquer nova tentativa.
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>

      <section aria-labelledby="capacidades">
        <Card>
          <TituloDePainel id="capacidades" texto="Capacidades declaradas" className="mb-3" />
          <h2 className="sr-only">Capacidades declaradas</h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-line text-left text-ink-muted">
                  <th className="py-2 pr-4 font-medium">Provedor</th>
                  <th className="py-2 pr-4 font-medium">Capacidade</th>
                  <th className="py-2 pr-4 font-medium">Estado</th>
                  <th className="py-2 pr-4 font-medium">O que impede</th>
                  <th className="py-2 pr-4 font-medium">Validada em</th>
                  <th className="py-2 font-medium">Se indisponível</th>
                </tr>
              </thead>
              <tbody>
                {data.capabilities.map((c) => (
                  <tr key={`${c.provider}:${c.capability}`} className="border-b border-line">
                    <td className="py-2 pr-4">{c.provider}</td>
                    <td className="py-2 pr-4">{c.capability}</td>
                    <td className="py-2 pr-4">
                      <span className={c.state === 'available' ? 'text-ok-ink' : 'text-warn-ink'}>
                        {CAPABILITY_STATE[c.state] ?? c.state}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-ink-muted">
                      {/*
                      O diagnóstico e o motivo escrito pela sondagem, e não só o
                      veredito. "Indisponível" não é acionável; "a conta está
                      ativa e sem meio de pagamento" é, e diz a quem recorrer.
                    */}
                      <span>{CAPABILITY_STATUS[c.status] ?? c.status}</span>
                      {c.notes === null ? null : (
                        <span className="mt-1 block text-ink-faint">{c.notes}</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-ink-faint">
                      {c.validated_at ? (
                        <>
                          {formatInstant(c.validated_at)}
                          <span className="mt-1 block">{c.source}</span>
                        </>
                      ) : (
                        /*
                        "Nunca sondada" e "sondada e indisponível" são estados
                        com ações opostas, e a data é o que os separa: sem ela,
                        mandaríamos conectar uma conta que já está conectada.
                      */
                        'nunca sondada'
                      )}
                    </td>
                    <td className="py-2 text-ink-faint">{c.fallback ?? 'não declarado'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </section>
    </div>
  );
}

/**
 * VERIFICAR A CONTA DE ANÚNCIOS (Paid Media SPEC §31).
 *
 * Este bloco existe porque rota entregue sem nada na tela que a chame não está
 * pronta. A sondagem é o que transforma "a conta foi conectada" em fato
 * verificado, e sem um botão ela seria uma funcionalidade inalcançável por quem
 * usa o produto.
 *
 * Três decisões:
 *
 * 1. **o botão só aparece habilitado quando o servidor diz que há o que
 *    chamar.** Não é dedução da tela: é o campo `meta_probe.available`. Um botão
 *    que falha sempre num ambiente sem credencial faria quem clicasse procurar o
 *    defeito na conta da Meta;
 *
 * 2. **o resultado é mostrado capacidade por capacidade, com o motivo.** Um
 *    "verificado com sucesso" esconderia exatamente o caso que importa, que é a
 *    verificação ter funcionado e dado negativo;
 *
 * 3. **"não sondado" e "sondado e indisponível" aparecem diferentes.** A
 *    primeira pede que alguém conecte a conta, a segunda que alguém resolva a
 *    restrição no painel da Meta. São duas pessoas diferentes.
 */
function SondagemDaMeta({
  dados,
  aoSondar,
}: {
  dados: IntegrationsResponse;
  aoSondar: () => void;
}) {
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<
    { capability: string; status: string; read_ok: boolean; notes: string }[] | null
  >(null);

  const probe = dados.meta_probe;

  async function verificar() {
    setErro(null);
    setResultado(null);
    setOcupado(true);
    try {
      const r = await api.rodarSondagemDaMeta();
      setResultado(r.results);
      // Recarrega a tabela: sem isto a tela mostraria o resultado novo em cima
      // e os vereditos antigos embaixo, e a de baixo é a que a pessoa acredita.
      aoSondar();
    } catch (e) {
      /*
        A mensagem vem do servidor. É ele que sabe dizer "não há conexão
        declarada neste ambiente", e uma frase genérica aqui mandaria alguém
        investigar a Meta quando o que falta é configuração daqui.
      */
      setErro(e instanceof ApiError ? e.message : 'Não foi possível falar com a API.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <section
      className="mt-6 rounded-card border border-line bg-card p-4 shadow-card"
      aria-labelledby="sondagem-da-meta"
    >
      <h2
        id="sondagem-da-meta"
        className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
      >
        Verificar a conta de anúncios da Meta
      </h2>
      <p className="mt-1 max-w-3xl text-xs text-ink-muted">
        Lê a conta conectada e grava o que ela responde: se pode rodar anúncio, quais objetivos de
        campanha ela aceita, a moeda e o fuso. Nada é criado nem gasto na Meta. Enquanto isso não
        for verificado, o portão de mídia paga recusa toda campanha, e é esse o comportamento
        correto.
      </p>

      {probe.available ? (
        <p className="mt-2 text-xs text-ink-faint">
          Conta {probe.ad_account_id} · Graph API {probe.api_version}
        </p>
      ) : (
        <p className="mt-2 rounded border border-warn-border bg-warn-surface p-3 text-xs text-warn-ink">
          Não há conexão com a Meta declarada neste ambiente, então não há o que verificar. Quem
          resolve isso configura a conta de anúncios e a credencial no ambiente, e não no painel da
          Meta.
        </p>
      )}

      <button
        type="button"
        onClick={verificar}
        disabled={ocupado || !probe.available}
        className="mt-3 rounded border border-accent-border bg-accent-strong px-3 py-1 text-sm text-white disabled:opacity-50"
      >
        {ocupado ? 'Verificando…' : 'Verificar agora'}
      </button>

      <ContaMedida conta={dados.meta_ad_account} />

      {erro === null ? null : (
        <p className="mt-3 rounded border border-danger-border bg-danger-surface p-3 text-xs">
          {erro}
        </p>
      )}

      {resultado === null ? null : (
        <ul className="mt-3 space-y-2">
          {resultado.map((r) => (
            <li key={r.capability} className="rounded border border-line p-3 text-xs">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium">{r.capability}</span>
                <span className={r.status === 'SUPPORTED' ? 'text-ok-ink' : 'text-warn-ink'}>
                  {CAPABILITY_STATUS[r.status] ?? r.status}
                </span>
              </div>
              <p className="mt-1 text-ink-muted">{r.notes}</p>
              {r.read_ok ? null : (
                /*
                  A distinção que decide a quem recorrer. Sem esta linha, uma
                  leitura que nem aconteceu apareceria como se a conta tivesse
                  respondido "não", e alguém iria mexer na conta da Meta para
                  resolver um problema de credencial ou de rede daqui.
                */
                <p className="mt-1 text-ink-faint">
                  A leitura não chegou a acontecer: isto não é a conta dizendo não, é a verificação
                  não ter conseguido perguntar.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Moeda, fuso e meio de pagamento, como a sondagem os MEDIU.
 *
 * Aparecem porque o resto do sistema hoje os assume: o adaptador de mídia paga
 * compara moeda antes de gastar, e o de Insights alinha janela por dia
 * presumindo UTC. Ver o valor medido é o que permite alguém notar que a conta
 * está em outra moeda antes da primeira campanha rodar, em vez de depois.
 */
function ContaMedida({ conta }: { conta: ContaDeAnuncioMedida | null }) {
  if (conta === null) {
    return (
      <p className="mt-3 text-xs text-ink-faint">
        Moeda e fuso da conta: nunca lidos. Enquanto ninguém verificar, o sistema trabalha com o que
        supõe, e não com o que a conta diz.
      </p>
    );
  }
  return (
    <p className="mt-3 text-xs text-ink-muted">
      Moeda {conta.currency ?? 'não informada'} · fuso {conta.timezone_name ?? 'não informado'}
      {conta.timezone_offset_hours_utc === null
        ? ''
        : ` (UTC${conta.timezone_offset_hours_utc >= 0 ? '+' : ''}${conta.timezone_offset_hours_utc})`}{' '}
      · meio de pagamento{' '}
      {conta.has_funding_source === null
        ? 'desconhecido'
        : conta.has_funding_source
          ? 'cadastrado'
          : 'ausente'}
      {conta.read_at === null ? '' : ` · lido em ${formatInstant(conta.read_at)}`}
    </p>
  );
}
